import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "../mock";
import { useReservation } from "./ReservationModal";
import RatingStatus from "./RatingStatus";

const SLIDES = [
  { src: IMAGES.ckSplash, label: "Island Ember", tag: "Signature Cocktail" },
  { src: IMAGES.tomahawk, label: "Tomahawk", tag: "Chef's Selection" },
  { src: IMAGES.ckPurple, label: "The Muse", tag: "Handcrafted Cocktail" },
  { src: IMAGES.lobster, label: "Lobster & Caviar", tag: "Chef's Selection" },
  { src: IMAGES.lamb, label: "Lamb Lollipops", tag: "Chef's Selection" },
  { src: IMAGES.ckPurpleDill, label: "Emerald's Kiss", tag: "Signature Cocktail" },
];

const DURATION = 4500;

// Rotating image with crossfade + Ken Burns — reused in both layouts
function Slideshow({ active, rounded = false }) {
  return (
    <>
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${rounded ? "overflow-hidden" : ""}`}
          style={{ opacity: i === active ? 1 : 0 }}
        >
          <img
            src={s.src}
            alt={s.label}
            className="w-full h-full object-cover ease-out"
            style={{
              transition: "transform 6500ms ease-out",
              transform: i === active ? "scale(1.12)" : "scale(1)",
            }}
          />
        </div>
      ))}
    </>
  );
}

function Dots({ active, setActive, className = "" }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {SLIDES.map((_, i) => (
        <button
          key={i}
          onClick={() => setActive(i)}
          aria-label={`Go to slide ${i + 1}`}
          className="h-1.5 rounded-full transition-all duration-500"
          style={{
            width: i === active ? 28 : 9,
            background: i === active ? "#C6A34E" : "rgba(246,239,227,0.35)",
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const { open } = useReservation();
  const [active, setActive] = useState(0);
  const next = useCallback(() => setActive((i) => (i + 1) % SLIDES.length), []);

  useEffect(() => {
    const t = setInterval(next, DURATION);
    return () => clearInterval(t);
  }, [next, active]);

  return (
    <>
      {/* ================= DESKTOP / TABLET — full-bleed cinematic slideshow ================= */}
      <section className="relative hidden lg:flex h-[100svh] min-h-[620px] w-full overflow-hidden items-center justify-center bg-nala-black">
        <div className="absolute inset-0">
          <Slideshow active={active} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-nala-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <p className="text-nala-gold tracking-luxe uppercase text-sm mb-6 opacity-0" style={{ animation: "fadeUp 0.9s ease 0.1s forwards" }}>
            Atlanta · Est. Buckhead
          </p>
          <h1 className="font-display text-7xl xl:text-8xl leading-[1.05] text-nala-cream opacity-0" style={{ animation: "fadeUp 1s ease 0.25s forwards" }}>
            The Celebration
            <br />
            <span className="text-gold-gradient">Destination</span>
          </h1>
          <p className="mt-8 font-serif-lux text-2xl text-nala-cream-muted max-w-2xl mx-auto leading-relaxed opacity-0" style={{ animation: "fadeUp 1s ease 0.45s forwards" }}>
            Elevated cuisine, handcrafted cocktails, and vibrant nightlife —
            where every occasion becomes unforgettable.
          </p>
          <div className="mt-10 flex flex-row items-center justify-center gap-4 opacity-0" style={{ animation: "fadeUp 1s ease 0.6s forwards" }}>
            <button onClick={open} className="btn-gold"><span>Reserve a Table</span></button>
            <Link to="/menu" className="btn-outline-gold"><span>Explore the Menu</span></Link>
          </div>
          <div className="mt-7 flex items-center justify-center opacity-0" style={{ animation: "fadeUp 1s ease 0.75s forwards" }}>
            <RatingStatus />
          </div>
        </div>

        <div className="absolute bottom-8 left-10 z-10 flex items-center gap-3">
          <span className="w-10 h-px bg-nala-gold/70" />
          <div key={active} style={{ animation: "fadeUp 0.7s ease forwards" }}>
            <p className="text-nala-gold text-[0.65rem] tracking-luxe uppercase">{SLIDES[active].tag}</p>
            <p className="font-display text-nala-cream text-sm tracking-wide-lux">{SLIDES[active].label}</p>
          </div>
        </div>
        <Dots active={active} setActive={setActive} className="absolute bottom-9 right-10 z-10" />
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-nala-gold/80 animate-bounce">
          <ChevronDown size={26} />
        </div>
      </section>

      {/* ================= MOBILE — Detour-style stacked layout with rotating image card ================= */}
      <section className="lg:hidden relative bg-nala-black pt-28 pb-14 px-6 overflow-hidden">
        {/* Soft ambient glow */}
        <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-nala-maroon/25 blur-[90px]" />
        <div className="pointer-events-none absolute top-40 -left-20 w-64 h-64 rounded-full bg-nala-gold/10 blur-[90px]" />

        <div className="relative z-10">
          {/* Rating / hours pill */}
          <div className="flex flex-wrap items-center gap-2 mb-6 opacity-0" style={{ animation: "fadeUp 0.7s ease 0.05s forwards" }}>
            <RatingStatus />
          </div>

          <p className="text-nala-gold tracking-luxe uppercase text-xs mb-4 opacity-0" style={{ animation: "fadeUp 0.8s ease 0.15s forwards" }}>
            Atlanta · Est. Buckhead
          </p>
          <h1 className="font-display text-[2.6rem] leading-[1.06] text-nala-cream opacity-0" style={{ animation: "fadeUp 0.9s ease 0.25s forwards" }}>
            The Celebration <span className="text-gold-gradient">Destination</span>
          </h1>
          <p className="mt-5 font-serif-lux text-lg text-nala-cream-muted leading-relaxed opacity-0" style={{ animation: "fadeUp 0.9s ease 0.4s forwards" }}>
            Elevated cuisine, handcrafted cocktails, and vibrant nightlife —
            where every occasion becomes unforgettable.
          </p>

          <div className="mt-7 flex flex-col gap-3 opacity-0" style={{ animation: "fadeUp 0.9s ease 0.55s forwards" }}>
            <button onClick={open} className="btn-gold w-full"><span>Reserve a Table</span></button>
            <Link to="/menu" className="btn-outline-gold w-full"><span>Explore the Menu</span></Link>
          </div>

          {/* Rotating image card */}
          <div className="mt-10 opacity-0" style={{ animation: "fadeUp 1s ease 0.7s forwards" }}>
            <div className="relative rounded-2xl overflow-hidden border border-nala-gold/25 shadow-2xl aspect-[4/5]">
              <Slideshow active={active} rounded />
              <div className="absolute inset-0 bg-gradient-to-t from-nala-black/85 via-transparent to-transparent" />

              {/* Caption badge */}
              <div key={active} className="absolute bottom-4 left-4 flex items-center gap-3" style={{ animation: "fadeUp 0.7s ease forwards" }}>
                <span className="w-8 h-px bg-nala-gold/70" />
                <div>
                  <p className="text-nala-gold text-[0.6rem] tracking-luxe uppercase">{SLIDES[active].tag}</p>
                  <p className="font-display text-nala-cream text-sm tracking-wide-lux">{SLIDES[active].label}</p>
                </div>
              </div>

              {/* Dots */}
              <Dots active={active} setActive={setActive} className="absolute bottom-5 right-4" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
